/**
 * Geolocalizzazione resilient: mai reject, mai blocca il render.
 * Su deny/timeout/errore → coordinate di un borgo di riferimento (Campli).
 */

/** Campli (TE) — borgo pilota 6amico, centro rete Abruzzo. */
export const GEO_FALLBACK = {
  lat: 42.7264,
  lng: 13.6861,
  slug: "campli",
  name: "Campli",
  source: "fallback",
};

export const GEO_FALLBACK_NOTICE =
  "Mostriamo i risultati generali. Attiva la geolocalizzazione per vedere i borghi più vicini a te.";

const DEFAULT_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 2500,
  maximumAge: 300000,
};

/**
 * @returns {Promise<{
 *   lat: number,
 *   lng: number,
 *   accuracy?: number|null,
 *   usedFallback: boolean,
 *   reason: 'ok'|'denied'|'unavailable'|'timeout'|'unsupported'|'error',
 *   slug?: string,
 *   name?: string,
 *   source: string,
 * }>}
 */
export function getPositionWithFallback(options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  return new Promise((resolve) => {
    let settled = false;

    const finishFallback = (reason) => {
      if (settled) return;
      settled = true;
      resolve({
        lat: GEO_FALLBACK.lat,
        lng: GEO_FALLBACK.lng,
        accuracy: null,
        usedFallback: true,
        reason,
        slug: GEO_FALLBACK.slug,
        name: GEO_FALLBACK.name,
        source: "fallback",
      });
    };

    const finishOk = (lat, lng, accuracy) => {
      if (settled) return;
      settled = true;
      resolve({
        lat,
        lng,
        accuracy: accuracy ?? null,
        usedFallback: false,
        reason: "ok",
        source: "geo",
      });
    };

    try {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        finishFallback("unsupported");
        return;
      }

      // Deny già noto → niente attesa del prompt / timeout
      try {
        if (navigator.permissions?.query) {
          navigator.permissions
            .query({ name: "geolocation" })
            .then((status) => {
              if (status?.state === "denied") finishFallback("denied");
            })
            .catch(() => {
              /* ignore — prosegui con getCurrentPosition */
            });
        }
      } catch {
        /* ignore */
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          try {
            const lat = pos?.coords?.latitude;
            const lng = pos?.coords?.longitude;
            if (
              typeof lat !== "number" ||
              typeof lng !== "number" ||
              Number.isNaN(lat) ||
              Number.isNaN(lng)
            ) {
              finishFallback("error");
              return;
            }
            finishOk(lat, lng, pos.coords?.accuracy);
          } catch {
            finishFallback("error");
          }
        },
        (err) => {
          const code = err?.code;
          const reason =
            code === 1 ? "denied" : code === 2 ? "unavailable" : code === 3 ? "timeout" : "error";
          finishFallback(reason);
        },
        opts,
      );
    } catch {
      finishFallback("error");
    }
  });
}
